import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Wand2,
  Settings,
  CheckCircle,
  Video,
  Image,
  FileText,
  Upload,
  X,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Content creation modes
const creationModes = [
  { 
    value: "video", 
    label: "Video", 
    description: "Text to video with AI", 
    icon: Video,
    color: "from-primary to-purple-600"
  },
  { 
    value: "photo", 
    label: "Photo", 
    description: "Photo to video instantly", 
    icon: Image,
    color: "from-accent-neon to-blue-600"
  },
  { 
    value: "content", 
    label: "Content", 
    description: "Marketing materials", 
    icon: FileText,
    color: "from-accent-warning to-orange-600"
  }
];

const videoTypes = [
  { value: "sales", label: "Sales", description: "Sell products & services", icon: "💰" },
  { value: "educational", label: "Educational", description: "Teach & inform", icon: "📚" },
  { value: "marketing", label: "Marketing", description: "Build your brand", icon: "📢" },
  { value: "explainer", label: "Explainer", description: "Simplify ideas", icon: "💡" }
];

const durations = [
  { value: 30, label: "30 sec" },
  { value: 60, label: "1 min" },
  { value: 120, label: "2 min" },
  { value: 300, label: "5 min" },
  { value: 600, label: "10 min" },
  { value: 1800, label: "30 min" }
];

const tones = [
  { value: "professional", label: "Professional" },
  { value: "casual", label: "Casual" },
  { value: "energetic", label: "Energetic" },
  { value: "serious", label: "Serious" }
];

export default function CreateVideo() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [showProSettings, setShowProSettings] = useState(false);
  
  // Main creation mode toggle
  const [creationMode, setCreationMode] = useState("video");
  
  // Photo mode state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [productName, setProductName] = useState("");
  
  const [formData, setFormData] = useState({
    video_type: "sales",
    topic: "",
    target_duration: 60,
    tone: "professional",
    generated_script: ""
  });

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Handle image upload for photo mode
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Generate video from photo
  const generateFromPhoto = async () => {
    if (!uploadedImage || !productName.trim()) {
      toast.error("Please upload an image and enter a product name");
      return;
    }

    setLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', uploadedImage);
      formDataUpload.append('product_name', productName);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/videos/generate-from-image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      toast.success("Video created! Redirecting to your library...");
      setTimeout(() => navigate("/library"), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate video from photo");
    } finally {
      setLoading(false);
    }
  };

  const generateScript = async () => {
    if (!formData.topic) {
      toast.error("Tell us what your video is about");
      return;
    }

    setScriptLoading(true);
    try {
      const response = await axios.post(`${API}/scripts/generate`, {
        topic: formData.topic,
        video_type: formData.video_type,
        target_duration: formData.target_duration,
        tone: formData.tone,
        llm_provider: "openai",
        additional_context: ""
      });

      updateForm("generated_script", response.data.script);
      toast.success("Script ready!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate script");
    } finally {
      setScriptLoading(false);
    }
  };

  const createAndGoToStudio = async () => {
    if (!formData.generated_script) {
      toast.error("Generate a script first");
      return;
    }

    setLoading(true);
    try {
      const videoTypeLabel = videoTypes.find(v => v.value === formData.video_type)?.label || "Video";
      const response = await axios.post(`${API}/projects`, {
        title: `${videoTypeLabel} - ${formData.topic.slice(0, 30)}`,
        description: formData.topic,
        video_type: formData.video_type,
        target_duration: formData.target_duration
      });

      await axios.put(`${API}/projects/${response.data.id}`, {
        script: formData.generated_script
      });

      navigate(`/studio/${response.data.id}`);
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="text-text-secondary hover:text-text-primary"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <span className="font-heading font-bold text-text-primary">Create Content</span>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="space-y-6">
          
          {/* ========== CREATION MODE TOGGLE ========== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Label className="text-text-primary mb-3 block font-medium text-center text-lg">
              What do you want to create?
            </Label>
            <div className="grid grid-cols-3 gap-3">
              {creationModes.map((mode) => {
                const IconComponent = mode.icon;
                const isSelected = creationMode === mode.value;
                return (
                  <motion.div
                    key={mode.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card
                      onClick={() => setCreationMode(mode.value)}
                      className={`cursor-pointer transition-all h-full ${
                        isSelected
                          ? 'border-2 border-primary bg-primary/10 shadow-lg shadow-primary/20'
                          : 'bg-background-paper border-border hover:border-primary/50'
                      }`}
                      data-testid={`mode-${mode.value}`}
                    >
                      <CardContent className="p-4 text-center">
                        <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${mode.color} flex items-center justify-center text-white mb-3`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div className={`font-bold text-base ${isSelected ? 'text-primary' : 'text-text-primary'}`}>
                          {mode.label}
                        </div>
                        <div className="text-xs text-text-muted mt-1">{mode.description}</div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-primary mx-auto mt-2" />
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <div className="border-t border-border pt-6" />

          {/* ========== PHOTO MODE ========== */}
          <AnimatePresence mode="wait">
            {creationMode === "photo" && (
              <motion.div
                key="photo-mode"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Image Upload */}
                <div>
                  <Label className="text-text-primary mb-3 block font-medium">
                    Upload Your Photo
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="photo-input"
                  />
                  
                  {!imagePreview ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
                    >
                      <Upload className="w-12 h-12 text-text-muted mx-auto mb-3" />
                      <p className="text-text-secondary font-medium">Click to upload your photo</p>
                      <p className="text-text-muted text-sm mt-1">JPG, PNG, WebP up to 10MB</p>
                    </div>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-64 object-cover"
                      />
                      <button
                        onClick={removeImage}
                        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Product Name */}
                <div>
                  <Label className="text-text-primary mb-2 block font-medium">
                    Product or Service Name
                  </Label>
                  <Input
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="e.g., Luxury Watch, Fitness App, Consulting Service"
                    className="bg-background-paper border-border text-text-primary"
                    data-testid="product-name-input"
                  />
                </div>

                {/* Generate Button */}
                <Button
                  onClick={generateFromPhoto}
                  disabled={loading || !uploadedImage || !productName.trim()}
                  className="w-full bg-accent-warning hover:bg-accent-warning/90 text-black py-6 font-bold"
                  data-testid="generate-photo-video-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating your video...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      Turn Photo into Video
                    </>
                  )}
                </Button>
              </motion.div>
            )}

            {/* ========== VIDEO MODE ========== */}
            {creationMode === "video" && (
              <motion.div
                key="video-mode"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Video Type */}
                <div>
                  <Label className="text-text-primary mb-3 block font-medium">
                    Video type
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {videoTypes.map((type) => (
                      <Card
                        key={type.value}
                        onClick={() => updateForm("video_type", type.value)}
                        className={`cursor-pointer transition-all ${
                          formData.video_type === type.value
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background-paper border-border hover:border-primary/50'
                        }`}
                        data-testid={`video-type-${type.value}`}
                      >
                        <CardContent className="p-4 flex items-center gap-3">
                          <span className="text-xl">{type.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-text-primary text-sm">{type.label}</div>
                            <div className="text-xs text-text-muted">{type.description}</div>
                          </div>
                          {formData.video_type === type.value && (
                            <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Topic */}
                <div>
                  <Label className="text-text-primary mb-2 block font-medium">
                    What's the video about?
                  </Label>
                  <Textarea
                    value={formData.topic}
                    onChange={(e) => updateForm("topic", e.target.value)}
                    placeholder="e.g., A video promoting our new fitness app that helps track workouts"
                    className="bg-background-paper border-border text-text-primary"
                    rows={3}
                    data-testid="topic-input"
                  />
                </div>

                {/* Pro Settings Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-background-paper border border-border">
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-text-muted" />
                    <span className="text-sm text-text-secondary">Pro settings</span>
                  </div>
                  <Switch 
                    checked={showProSettings} 
                    onCheckedChange={setShowProSettings}
                    data-testid="pro-settings-toggle"
                  />
                </div>

                {/* Pro Settings Panel */}
                {showProSettings && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-4 p-4 rounded-lg bg-background-subtle border border-border"
                  >
                    <div>
                      <Label className="text-text-primary text-sm mb-2 block">Duration</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {durations.map((d) => (
                          <button
                            key={d.value}
                            onClick={() => updateForm("target_duration", d.value)}
                            className={`py-2 px-3 rounded-lg text-sm transition-all ${
                              formData.target_duration === d.value
                                ? 'bg-primary text-white'
                                : 'bg-background-paper text-text-secondary hover:bg-background border border-border'
                            }`}
                            data-testid={`duration-${d.value}`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-text-primary text-sm mb-2 block">Tone</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {tones.map((t) => (
                          <button
                            key={t.value}
                            onClick={() => updateForm("tone", t.value)}
                            className={`py-2 px-3 rounded-lg text-sm transition-all ${
                              formData.tone === t.value
                                ? 'bg-primary text-white'
                                : 'bg-background-paper text-text-secondary hover:bg-background border border-border'
                            }`}
                            data-testid={`tone-${t.value}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Generate Script Button */}
                <Button
                  onClick={generateScript}
                  disabled={scriptLoading || !formData.topic}
                  className="w-full bg-primary hover:bg-primary-hover text-white py-6"
                  data-testid="generate-script-btn"
                >
                  {scriptLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Writing script...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Write Script
                    </>
                  )}
                </Button>

                {/* Generated Script */}
                {formData.generated_script && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    <Label className="text-text-primary font-medium">Your Script</Label>
                    <Textarea
                      value={formData.generated_script}
                      onChange={(e) => updateForm("generated_script", e.target.value)}
                      className="bg-background-paper border-border text-text-primary text-sm leading-relaxed"
                      rows={10}
                      data-testid="script-output"
                    />
                    
                    <Button
                      onClick={createAndGoToStudio}
                      disabled={loading}
                      className="w-full bg-accent-success hover:bg-accent-success/90 text-white py-6"
                      data-testid="continue-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <>
                          Make Video
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* ========== CONTENT MODE ========== */}
            {creationMode === "content" && (
              <motion.div
                key="content-mode"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-text-muted mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-text-primary mb-2">
                    Marketing Content Coming Soon
                  </h3>
                  <p className="text-text-secondary max-w-sm mx-auto">
                    Create social media posts, email campaigns, ad copy, and more. 
                    This feature is under development.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6"
                    onClick={() => setCreationMode("video")}
                  >
                    Create a Video Instead
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
